import { useCallback, useEffect, useState, type ReactNode } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

/** Swipeable on phones; the existing grid remains unchanged on larger screens. */
export default function MobileHomeCarousel({ children, label, desktopClassName, narrow = false, initialIndex = 0 }: {
  children: ReactNode[];
  label: string;
  desktopClassName: string;
  narrow?: boolean;
  initialIndex?: number;
}) {
  const mobile = useIsMobile();
  const [viewportRef, api] = useEmblaCarousel({ align: "start", loop: true, active: mobile, startIndex: initialIndex });
  const [selected, setSelected] = useState(0);
  const update = useCallback(() => setSelected(api?.selectedScrollSnap() ?? 0), [api]);
  useEffect(() => {
    if (mobile && api) api.scrollTo(initialIndex, true);
  }, [api, mobile, initialIndex]);
  useEffect(() => {
    if (!api) return;
    update();
    api.on("select", update).on("reInit", update);
    return () => { api.off("select", update).off("reInit", update); };
  }, [api, update]);

  return (
    <div className={`ns-mobile-carousel ${narrow ? "ns-mobile-carousel-narrow" : ""}`} role="region" aria-label={label} aria-roledescription="carousel">
      <div ref={viewportRef} className="ns-mobile-carousel-viewport">
        <div className={`ns-mobile-carousel-track ${desktopClassName}`}>
          {children.map((child, index) => <div key={index} className="ns-mobile-carousel-slide">{child}</div>)}
        </div>
      </div>
      <div className="mt-8 flex items-center justify-between md:hidden">
        <Button variant="ghost" size="icon" aria-label={`Previous ${label}`} onClick={() => api?.scrollPrev()} className="h-11 w-11 text-inherit hover:bg-ns-plum/10"><ChevronLeft className="h-5 w-5" /></Button>
        <div className="flex items-center">
          {children.map((_, index) => <Button key={index} variant="ghost" aria-label={`Show ${label} ${index + 1}`} aria-current={selected === index} onClick={() => api?.scrollTo(index)} className="h-8 w-4 rounded-none p-0 hover:bg-transparent"><span className={`h-2 w-2 rounded-full bg-current ${selected === index ? "opacity-100" : "opacity-30"}`} /></Button>)}
        </div>
        <Button variant="ghost" size="icon" aria-label={`Next ${label}`} onClick={() => api?.scrollNext()} className="h-11 w-11 text-inherit hover:bg-ns-plum/10"><ChevronRight className="h-5 w-5" /></Button>
      </div>
    </div>
  );
}